import { StrictMode } from "react";
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx'
import './index.css'

// --- Service worker cleanup -------------------------------------------------
// The marketing site no longer uses an offline app-shell cache. Older visitors
// may still have the previous Workbox worker installed, which can keep serving
// stale HTML. Register the kill-switch worker at the same path (it evicts the
// old registration and reloads the tab), then make sure nothing is left behind.

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes('id-preview--') ||
  window.location.hostname.includes('preview--') ||
  window.location.hostname.includes('lovableproject.com') ||
  window.location.hostname.includes('lovableproject-dev.com') ||
  window.location.hostname.includes('beta.lovable.dev');

async function clearAllCaches() {
  if (!('caches' in window)) return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    /* no-op */
  }
}

async function cleanupServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length > 0) {
      if (isPreviewHost || isInIframe || !import.meta.env.PROD) {
        await Promise.allSettled(registrations.map((r) => r.unregister()));
      } else {
        // Same-path replacement worker evicts the old registration on returning devices.
        try {
          await navigator.serviceWorker.register('/sw.js');
        } catch {
          await Promise.allSettled(registrations.map((r) => r.unregister()));
        }
      }
    }
  } catch {
    /* no-op */
  }
  await clearAllCaches();
}

void cleanupServiceWorkers();

// Render first, defer everything else
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);

// Start the freshness watcher immediately so returning visitors see new builds.
import('./lib/auto-reload').then(({ startAutoReload }) => {
  startAutoReload();
});

// Defer all non-critical initialization to after the page is interactive
const deferInit = () => {
  import('./lib/web-vitals').then(({ reportWebVitals }) => {
    import('./integrations/analytics/plausible').then(({ analytics, trackPageView }) => {
      reportWebVitals((metric) => {
        analytics.trackPerformance(metric.name, metric.value, metric.rating);
      });
      trackPageView();
    });
  });

  import('./lib/enhanced-analytics').then(({ enhancedAnalytics }) => {
    enhancedAnalytics.startSession();
  });

  if (process.env.NODE_ENV === 'production') {
    import('./integrations/supabase/client').then(({ supabase }) => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          import('./integrations/session-replay/posthog').then(({ identifyUser }) => {
            identifyUser(session.user.id, {
              email: session.user.email,
            });
          });
        }
      });
    });
  }

  import('./lib/performance-optimizations').then(({ optimizeCriticalPath }) => {
    optimizeCriticalPath();
  });
};

if (typeof requestIdleCallback === 'function') {
  requestIdleCallback(deferInit);
} else {
  setTimeout(deferInit, 2000);
}
